/**
 * Utilitaire pour gérer les téléchargements de fichiers de manière standardisée
 * Propose à l'utilisateur de choisir où enregistrer le fichier
 */

import { TypeUtils } from './ErrorUtils';

/**
 * Télécharge un fichier via une URL avec authentification
 * @param {string} url - URL du fichier à télécharger
 * @param {string} filename - Nom suggéré pour le fichier
 * @param {string} token - Token d'authentification (optionnel)
 * @param {Object} headers - Headers supplémentaires (optionnel)
 * @returns {Promise} Promise qui se résout quand le téléchargement est terminé
 */
export function downloadFile(url, filename, token = null, headers = {}) {
  return new Promise((resolve, reject) => {
    
    // Préparer les headers
    const requestHeaders = {
      ...headers
    };
    
    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`;
    }
    
    // Effectuer la requête
    fetch(url, {
      method: 'GET',
      headers: requestHeaders
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status} - ${response.statusText}`);
        }
        return response.blob();
      })
      .then(blob => {
      // Créer une URL temporaire pour le blob
        const blobUrl = window.URL.createObjectURL(blob);
      
        // Créer un lien de téléchargement
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
      
        // Déclencher le téléchargement
        document.body.appendChild(link);
        link.click();
      
        // Nettoyer
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      
        resolve(filename);
      })
      .catch(error => {
        reject(error);
      });
  });
}

/**
 * Télécharge un blob directement
 * @param {Blob} blob - Le blob à télécharger
 * @param {string} filename - Nom suggéré pour le fichier
 * @returns {Promise} Promise qui se résout quand le téléchargement est terminé
 */
export function downloadBlob(blob, filename) {
  return new Promise((resolve) => {
    
    // Créer une URL temporaire pour le blob
    const blobUrl = window.URL.createObjectURL(blob);
    
    // Créer un lien de téléchargement
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    
    // Déclencher le téléchargement
    document.body.appendChild(link);
    link.click();
    
    // Nettoyer
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    
    resolve(filename);
  });
}

/**
 * Wrapper pour remplacer downloadjs avec notre utilitaire
 * @param {Blob|string} data - Blob ou URL à télécharger
 * @param {string} filename - Nom du fichier
 * @param {string} token - Token d'authentification (si data est une URL)
 */
export function download(data, filename, token = null) {
  if (data instanceof Blob) {
    return downloadBlob(data, filename);
  } else if (TypeUtils.isString(data)) {
    return downloadFile(data, filename, token);
  } else {
    throw new Error('Le paramètre data doit être un Blob ou une URL string');
  }
}

export default {
  downloadFile,
  downloadBlob,
  download
};
